import { useEffect, useState } from 'react'
import type { UsageSnapshotResponse } from '@super/shared-types'
import { billingClient } from '@/lib/api/billing.client'
import { cn } from '@/lib/utils'

const WARNING_THRESHOLD = 0.8
const OVERAGE_THRESHOLD = 1.0

const UNIT_LABEL: Record<'minutes' | 'sms', string> = {
  minutes: 'min',
  sms: 'SMS',
}

export function UsageTile() {
  const [snapshot, setSnapshot] = useState<
    UsageSnapshotResponse | null | undefined
  >(undefined)

  useEffect(() => {
    billingClient
      .getUsage()
      .then(s => setSnapshot(s))
      .catch(err => {
        console.error('Failed to load usage', err)
        setSnapshot(null)
      })
  }, [])

  if (snapshot === undefined) return <UsageSkeleton />
  if (snapshot === null) return null

  return (
    <section className="border-border rounded-md border p-6">
      <div className="flex items-baseline justify-between pb-4">
        <h2 className="text-sm font-medium">
          {new Date(snapshot.cycleStartsAt).toLocaleDateString(undefined, {
            month: 'long',
          })}
          's cycle usage
        </h2>
        <span className="text-muted-foreground text-xs">
          Resets {new Date(snapshot.cycleEndsAt).toLocaleDateString()}
        </span>
      </div>
      <div className="flex flex-col gap-6">
        <UsageBar
          resource="minutes"
          included={snapshot.includedMinutes}
          remaining={snapshot.minutesRemaining}
        />
        <UsageBar
          resource="sms"
          included={snapshot.includedSms}
          remaining={snapshot.smsRemaining}
        />
      </div>
    </section>
  )
}

function UsageBar({
  resource,
  included,
  remaining,
}: {
  resource: 'minutes' | 'sms'
  included: number
  remaining: number
}) {
  const used = Math.max(0, included - remaining)
  const usedRatio = included > 0 ? used / included : 0
  const usedPercent = Math.round(usedRatio * 100)
  const overage = Math.max(0, used - included)
  const nearLimit = usedRatio >= WARNING_THRESHOLD
  const overLimit = usedRatio >= OVERAGE_THRESHOLD
  const unit = UNIT_LABEL[resource]

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-3">
        <span className="text-sm font-medium capitalize">{resource}</span>
        <span
          className={cn(
            'text-xs',
            overLimit
              ? 'text-amber-700'
              : nearLimit
                ? 'text-orange-600'
                : 'text-muted-foreground',
          )}
        >
          {used.toLocaleString()} of {included.toLocaleString()} {unit}
          {overage > 0 ? ` (+${overage.toLocaleString()} over)` : ''}
        </span>
        <span className="text-foreground ml-auto text-sm font-semibold tabular-nums">
          {usedPercent}% used
        </span>
      </div>
      <div className="bg-muted h-2 overflow-hidden rounded-full">
        <div
          className={cn(
            'h-full transition-all',
            overLimit
              ? 'bg-amber-500'
              : nearLimit
                ? 'bg-orange-500'
                : 'bg-foreground/60',
          )}
          style={{ width: `${Math.min(100, usedRatio * 100)}%` }}
        />
      </div>
    </div>
  )
}

function UsageSkeleton() {
  return (
    <section className="border-border rounded-md border p-6">
      <div className="bg-muted h-4 w-32 animate-pulse rounded" />
      <div className="mt-4 flex flex-col gap-4">
        <div className="bg-muted h-2 w-full animate-pulse rounded-full" />
        <div className="bg-muted h-2 w-full animate-pulse rounded-full" />
      </div>
    </section>
  )
}
